import classnames from 'classnames';
import { head, values } from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import ErrorModal from 'components/Practice/shared/ErrorModal';
import recordingPermissionErrorTypes from 'constants/recordingPermissionErrorTypes.js';
import trackEvent from 'misc/trackEvent';
import UniversalPlayer from 'components/shared/UniversalPlayer';
import RetryButton from 'components/Practice/shared/RetryButton';
import ElementLoading from 'components/shared/ElementLoading/ElementLoading';
import DropTarget from '../dropTarget';
import ActionButtons from './ActionButtons';
import CompletedRecording from './CompletedRecording';

class MediaRecording extends Component {
  state = {
    audio: this.props.browserSupport.audio,
    errorType: null,
    importTimingStarted: null,
    loading: true,
    recording: false,
    uploading: false,
    screen: this.props.browserSupport.screen,
    token: this.props.content.token,
    isSeismic: !!this.props.content.seismic_audio_id,
    updating: false,
    webcam: this.props.browserSupport.webcam,
    mediaUploading: false,
    fileValue: '',
    tryAgainDisabled: false,
  };

  fileInput = React.createRef();

  componentDidMount() {
    window.hippoWidget.onLoad(() => {
      this.setState({ loading: false });
    });
  }

  static supportedTypes = ['webcam', 'screen', 'audio'];

  updateTypes = () => {
    this.props.handleUpdate({
      types: MediaRecording.supportedTypes.filter(type => this.state[type]),
    });
  };

  audioRecordingConfig = {
    screenRecord: false,
    audio: this.state.audio,
    webCam: false,
    resolution: '1080',
    tabRecording: false,
    separateLayer: false,
    fromPopup: true,
    initiator: 'generic_embed',
  };

  trackRecordingEvent = (event, data) => {
    if (this.props.isExampleRecording) return;
    trackEvent(event, data);
  };

  handleOptionChange = e => {
    this.setState({ [e.target.name]: e.target.checked });
  };

  handleRecord = () => {
    this.setState({ recording: true, mediaUploading: true }, this.updateTypes);
    const config = this.audioRecordingConfig;

    window.hippoWidget.on('record_error', this.showErrorModal);
    window.hippoWidget.startRecording(config, this.props.currentUserId);
    window.hippoWidget.on('record_initiated', this.trackRecordingEvents);
    window.hippoWidget.on('record_started', this.trackRecordingStarted);
    window.hippoWidget.on('record_aborted', this.trackRecordingAborted);
    window.hippoWidget.on('record_stopped', this.trackRecordingStopped);

    window.hippoWidget.on('source_uploaded', () => {
      this.sourceUploaded();
      this.trackSourceUploaded(false);
    });
    window.hippoWidget.on('video_submitted', data => {
      this.videoSubmitted(data);
      this.trackVideoSubmitted(false);
    });
    window.hippoWidget.on('video_cancelled', this.videoCancelled);
  };

  videoCancelled = data => {
    this.setState({ uploading: false, fileValue: '' });
    this.handleReload(data);
    this.trackVideoCancelled(false);
  };

  handleUploadError = error => {
    this.trackImportFailure(error);
    if (error.reason === 'invalid_file_format') {
      this.setState({
        errorType: error.reason + '_all',
      });
    } else if (error.reason === 'size_limit_exceeded') {
      this.setState({
        errorType: error.reason,
      });
    } else if (error.reason === 'processing_error') {
      if (error.error.includes('Media length is too short')) {
        this.setState({
          errorType: 'video_too_small',
        });
      } else if (
        error.error.includes('Video Codec Unidentified') ||
        error.error.includes('Audio Codec Unidentified')
      ) {
        this.setState({
          errorType: 'unidentified_codec',
        });
      }
    }
    this.setState({ uploading: false });
  };

  uploadFile = file => {
    this.setState({ recording: true }, this.updateTypes);
    window.hippoWidget.on('import_failed', this.handleUploadError);
    window.hippoWidget.importFile(file, this.props.currentUserId);
    window.hippoWidget.on('import_progress', properties => {
      if (properties.operation === 'started') {
        this.trackImportStarted();
      }
    });
    window.hippoWidget.on('source_uploaded', () => {
      this.trackSourceUploaded(true);
    });
    window.hippoWidget.on('video_submitted', data => {
      this.videoSubmitted(data);
      this.trackVideoSubmitted(true);
    });
    window.hippoWidget.on('import_complete', data => {
      this.trackRecordingData(data);
      this.trackImportComplete(data);
    });
    window.hippoWidget.on('video_cancelled', data => {
      this.handleReload(data);
      this.trackVideoCancelled(true);
    });
  };

  handleFileChange = (e, droppedFiles) => {
    const file = head(droppedFiles || e.target.files);

    if (!file) {
      return;
    }

    this.setState({ uploading: true }, () => {
      this.uploadFile(file);
    });
  };

  trackingEventParameters = () => ({
    lesson_section_element_id: this.props.id,
    progress_id: null,
    question_id: null,
    recording_type: 'audio',
  });

  trackVideoCancelled = isUpload => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: isUpload,
    };
    this.trackRecordingEvent('hippo_video/recording_cancelled', eventData);
  };

  handleReload = () => {
    const { browserSupport } = this.props;
    this.setState(
      {
        audio: browserSupport.audio,
        recording: false,
        screen: false,
        token: null,
        webcam: false,
        uploading: false,
        mediaUploading: false,
      },
      () => {
        // When reloading the recording buttons, we want to reset the token to null in our database
        this.props.handleUpdate({ token: null, provider: null });
        this.updateTypes();
      },
    );
  };

  sourceUploaded = event => {
    /*
      This event happens once we have uploaded a video to Hippo. If a video takes a
      long time to upload, we want to make sure we are only saving a reference to it
      once the whole thing has uploaded. Because of the way Hippo works, you can click
      the Submit button before the video has fully uploaded,  which can cause you to
      lose your video. This event happens in a couple files: here, RecordMenu.jsx,
      and Question.jsx
    */
    const { handleUpdate, applyChanges } = this.props;
    const { token } = this.state;
    if (token) {
      handleUpdate({ token, provider: 'hippo' }).then(() =>
        applyChanges({ token, provider: 'hippo' }),
      );
    }
    this.setState({
      mediaUploading: false,
    });
  };

  videoSubmitted = event => {
    const { handleUpdate, applyChanges } = this.props;
    // We're using regex to extract the token from the recording url
    const recordingUrlRegex = /\/video\/play\/(.+)$/g;
    const token = recordingUrlRegex.exec(event.recorded_url)[1];
    this.setState({ token, recording: false }, () => {
      if (!this.state.mediaUploading) {
        handleUpdate({ token, provider: 'hippo' }).then(() =>
          applyChanges({ token, provider: 'hippo' }),
        );
      }
    });
  };

  trackRecordingEvents = data => {
    this.trackRecordingInitiated(data);
    this.trackRecordingData(data);
  };

  trackRecordingInitiated = data => {
    const eventData = {
      ...this.trackingEventParameters(),
      token: data.token,
    };
    this.trackRecordingEvent('hippo_video/recording_initiated', eventData);
  };

  trackRecordingData = data => {
    const eventData = { asset_id: data.asset_id, token: data.token };
    this.trackRecordingEvent('recording_metadata', eventData);
  };

  trackVideoSubmitted = isUpload => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: isUpload,
    };
    this.trackRecordingEvent('hippo_video/video_submitted', eventData);
  };

  trackRecordingStarted = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };
    this.trackRecordingEvent('hippo_video/recording_started', eventData);
  };

  trackRecordingAborted = () => {
    const eventData = {
      lesson_section_element_id: this.props.id,
      question_id: null,
      progress_id: null,
      recording_type: 'audio',
    };
    this.trackRecordingEvent('hippo_video/recording_aborted', eventData);
  };

  trackRecordingStopped = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };
    this.trackRecordingEvent('hippo_video/recording_stopped', eventData);
  };

  trackSourceUploaded = isUpload => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: isUpload,
    };
    this.trackRecordingEvent('hippo_video/source_uploaded', eventData);
  };

  trackPermissionDenied = errorType => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: false,
      error_type: errorType,
    };
    this.trackRecordingEvent('hippo_video/permission_denied', eventData);
  };

  trackImportFailure = error => {
    const eventData = { element_id: this.props.id, error };
    this.trackRecordingEvent('recording_in_editor/upload_failed', eventData);
  };

  trackImportStarted = () => {
    this.setState({ importTimingStarted: performance.now() });
    this.trackRecordingEvent('recording_in_editor/upload_started', {
      element_id: this.props.id,
    });
  };

  trackImportComplete = data => {
    const { importTimingStarted } = this.state;
    const importTimingComplete = performance.now();
    const importTimingFinal = importTimingComplete - importTimingStarted;
    this.trackRecordingEvent('recording_in_editor/upload_complete', {
      element_id: this.props.id,
      token: data.token,
      duration: data.media_duration,
      total_import_time: importTimingFinal,
    });
  };

  trackRecordingError = errorType => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: false,
      error_type: errorType,
    };
    this.trackRecordingEvent('hippo_video/recording_error', eventData);
  };

  showErrorModal = response => {
    const errorType = response.error;

    if (Object.values(recordingPermissionErrorTypes).includes(errorType)) {
      this.trackPermissionDenied(errorType);
    } else {
      this.trackRecordingError(errorType);
    }
    if (
      errorType !==
      recordingPermissionErrorTypes.HIPPO_SCREEN_PERMISSION_DENIED_ERROR
    ) {
      this.setState({ errorType });
    }
  };

  handleResetError = () => {
    this.setState({ errorType: null });
  };

  saveMedia = data => {
    this.setState({ updating: true, webcam: false, screen: false });
    this.updateTypes();
    const mediaData = {
      file_name: data?.name,
      token: data?.token || data?.storageId,
      isSeismicAudio: true,
      fileFormat: data?.fileFormat,
      containerId: data?.containerId,
      size: data?.size,
      duration: data?.duration,
      storageService: data?.storageService,
    };
    this.props
      .handleUpdate(mediaData)
      .then(() => this.setState({ isSeismic: true, updating: false }));
  };

  setTryAgainDisabled = state => {
    this.setState({ tryAgainDisabled: state });
  };

  handleTryAgain = () => {
    this.setTryAgainDisabled(true);
    this.setState({ updating: true, isSeismic: false });
    this.props
      .handleUpdate({ token: null, provider: null, isSeismicAudio: true })
      .then(() => {
        this.setTryAgainDisabled(false);
        const { browserSupport } = this.props;
        this.setState({
          audio: browserSupport.audio,
          updating: false,
          token: null,
          recording: false,
          screen: false,
          webcam: false,
          uploading: false,
          mediaUploading: false,
        });

        this.updateTypes();
      });
  };

  render() {
    const {
      connectDropTarget,
      isOver,
      isExampleRecording = false,
    } = this.props;
    const { fileValue, uploading } = this.state;
    const url =
      (this.props.content.preview_base || this.props.content.previewBase) +
      this.state.token +
      '?type=lessonly';
    const audioId = this.props.content.id;
    const LOADING_TEXT = t('questions.types.content.loading');
    const embedUrl = this.state.token ? url : null;
    // If no recording types are checked, we need to disable the record button
    const noCheckedOptions =
      !this.state.audio && !this.state.screen && !this.state.webcam;
    // If all values in the browserSupport object are false, we need to disable the record button
    const isRecordingDisabled =
      values(this.props.browserSupport).every(supported => !supported) ||
      noCheckedOptions;
    const recordingText = () => {
      return t('lessons.editor.media_recording.audio.record');
    };
    const uploadText = () => {
      return t('lessons.editor.media_recording.audio.upload');
    };

    return connectDropTarget(
      <div>
        {!this.state.token && !this.state.isSeismic && !this.state.updating && (
          <div
            className={classnames({
              'EditorElement-recording':
                !isExampleRecording &&
                this.props.seismicAudioElementRecordingEnabled,
              'd-f jc-c': isExampleRecording,
            })}
          >
            <div
              className={classnames(
                {
                  'is-over': isOver,
                  'EditorElement-uuc-audio':
                    this.props.seismicAudioElementUploadEnabled ||
                    this.props.seismicAudioPracticeUploadEnabled,
                },
                'EditorElement-uploadArea',
                'EditorElement-uploadArea--dropZone pt-0',
              )}
            >
              <ActionButtons
                loading={this.state.loading}
                onRecord={this.handleRecord}
                onUpload={this.handleFileChange}
                saveMedia={
                  this.props.seismicAudioPracticeUploadEnabled
                    ? this.props.submitRecording
                    : this.saveMedia
                }
                isDisabled={isRecordingDisabled}
                recordText={recordingText()}
                uploadText={uploadText()}
                fileInput={this.fileInput}
                fileValue={fileValue}
                isUploading={uploading}
                isSeismicAudio
                seismicAudioUploadEnabled={
                  this.props.seismicAudioElementUploadEnabled ||
                  this.props.seismicAudioPracticeUploadEnabled
                }
                seismicAudioElementRecordingEnabled={
                  this.props.seismicAudioElementRecordingEnabled
                }
              />
            </div>
          </div>
        )}
        {this.state.token && (
          <CompletedRecording
            embedUrl={embedUrl}
            handleReload={this.handleReload}
          />
        )}
        {this.state.updating && (
          <div className="EditorElement-recordingComplete">
            <div className="d-f fd-c w-100p">
              <div className="Video-redo p-8 ta-r">
                <RetryButton disabled />
              </div>
              <ElementLoading isLoading text={LOADING_TEXT} />
            </div>
          </div>
        )}
        {this.state.isSeismic && (
          <div className="EditorElement-recordingComplete">
            <div className="Video-redo ta-r p-8">
              <RetryButton
                disabled={this.state.tryAgainDisabled}
                setTryAgainDisabled={this.setTryAgainDisabled}
                onClick={this.handleTryAgain}
              />
            </div>
            <UniversalPlayer
              audioId={audioId}
              audioType="MediaRecording"
              isPractice
            />
          </div>
        )}
        {this.state.errorType && (
          <ErrorModal
            errorType={this.state.errorType}
            onAction={this.handleResetError}
          />
        )}
      </div>,
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  browserSupport: ownProps.browserSupport || state.meta.browserSupport,
  currentUserId: state.editor.currentUserId,
});

export default connect(mapStateToProps)(DropTarget(MediaRecording));
